import { DownloadSimpleIcon, FileCsvIcon } from "@phosphor-icons/react";
import { type ReactNode, type RefObject, useRef, useState } from "react";
import { ChatComponentExportToolbar } from "@/components/ui/chat/chat-component-export/ChatComponentExportToolbar";
import { HeraldCardCatalogTableDescendantProvider } from "@/lib/chat/heraldCardCatalogTableRegistrationContext";
import { HeraldChatPngExportSuppressedProvider } from "@/lib/chat/heraldChatPngExportSuppressedContext";
import { useExportComponentAsPng } from "@/lib/hooks/chat/useExportComponentAsPng";
import { useExportRenderedTableAsCsv } from "@/lib/hooks/chat/useExportRenderedTableAsCsv";
import type { HeraldExportMode } from "./heraldExportableComponents";

const HeraldCatalogExportToolbar = ({
  targetRef,
  componentType,
  mode,
}: {
  targetRef: RefObject<HTMLElement | null>;
  componentType: string;
  mode: HeraldExportMode;
}) => {
  const { exportAsPng, isPending } = useExportComponentAsPng({
    targetRef,
    componentType,
  });
  const { exportAsCsv } = useExportRenderedTableAsCsv({
    targetRef,
    componentType,
  });

  const pngAction = {
    id: "png" as const,
    icon: <DownloadSimpleIcon className="text-gray-700" size={14} />,
    "aria-label": isPending ? "Exporting PNG…" : "Download as PNG",
    tooltipLabel: isPending ? "Exporting PNG…" : "Download as PNG",
    onPress: () => {
      void exportAsPng();
    },
    isPending,
    isDisabled: isPending,
  };

  const csvAction = {
    id: "csv" as const,
    icon: <FileCsvIcon className="text-gray-700" size={16} />,
    "aria-label": "Download as CSV",
    tooltipLabel: "Download as CSV",
    onPress: exportAsCsv,
  };

  const actions = mode === "png" ? [pngAction] : [pngAction, csvAction];

  return <ChatComponentExportToolbar actions={actions} />;
};

export const HeraldCatalogExportFrame = ({
  componentType,
  mode,
  children,
}: {
  componentType: string;
  mode: HeraldExportMode;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [cardHasCatalogTableDescendant, setCardHasCatalogTableDescendant] =
    useState(false);

  const toolbarMode: HeraldExportMode =
    componentType === "Card" && mode === "png" && cardHasCatalogTableDescendant
      ? "csv"
      : mode;

  const subtree =
    componentType === "Card" && mode === "png" ? (
      <HeraldCardCatalogTableDescendantProvider
        onHasCatalogTableChange={setCardHasCatalogTableDescendant}
      >
        <HeraldChatPngExportSuppressedProvider>
          {children}
        </HeraldChatPngExportSuppressedProvider>
      </HeraldCardCatalogTableDescendantProvider>
    ) : (
      children
    );
  return (
    <div ref={ref} className="relative w-full min-w-0 group/chat-export">
      {subtree}
      <HeraldCatalogExportToolbar
        targetRef={ref}
        componentType={componentType}
        mode={toolbarMode}
      />
    </div>
  );
};
